(function initialiseArtboxCollectionLifecycle() {
  const lifecycleKey = Symbol.for('artbox.collection.lifecycle');
  const existingLifecycle = window[lifecycleKey];

  if (existingLifecycle) {
    existingLifecycle.initialise(document);
    return;
  }

  const records = new WeakMap();
  const cardRecords = new WeakMap();
  const preparedSwatchImages = new Map();
  const filterRequests = new WeakMap();
  const deviceStorageKey = 'preferredDeviceModel';
  let facetSequence = 0;

  function loadSwatchImage(image) {
    const source = image.dataset.artboxSwatchSrc;
    if (!source) return;
    if (image.closest('[data-artbox-recommendations-grid]')) image.loading = 'eager';
    image.src = source;
    delete image.dataset.artboxSwatchSrc;
  }

  function observeSwatchImages(root, record) {
    if (!record.swatchImageObserver && 'IntersectionObserver' in window) {
      record.swatchImageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const images = entry.target.matches('[data-artbox-recommendations-grid]')
            ? entry.target.querySelectorAll('img[data-artbox-swatch-src]')
            : [entry.target];
          images.forEach(loadSwatchImage);
          if (entry.target.matches('[data-artbox-linked-card]')) prepareCardSwatches(entry.target);
          else if (entry.target.matches('[data-artbox-recommendations-grid]')) {
            entry.target.querySelectorAll('[data-artbox-linked-card]').forEach(prepareCardSwatches);
          }
          observer.unobserve(entry.target);
          record.observedSwatchTargets.delete(entry.target);
        });
      }, { rootMargin: '600px 0px' });
    }
    record.observedSwatchTargets.forEach((target) => {
      if (root.contains(target)) return;
      record.swatchImageObserver?.unobserve(target);
      record.observedSwatchTargets.delete(target);
    });
    root.querySelectorAll('img[data-artbox-swatch-src]').forEach((image) => {
      if (record.swatchImageObserver) {
        const target = image.closest('[data-artbox-recommendations-grid]') || image;
        if (record.observedSwatchTargets.has(target)) return;
        record.observedSwatchTargets.add(target);
        record.swatchImageObserver.observe(target);
      } else loadSwatchImage(image);
    });
    root.querySelectorAll('[data-artbox-linked-card]').forEach((card) => {
      if (!card.querySelector('[data-artbox-color-chooser-open]') || cardRecords.get(card)?.swatchesPrepared) return;
      const target = card.closest('[data-artbox-recommendations-grid]') || card;
      if (record.swatchImageObserver) {
        if (record.observedSwatchTargets.has(target)) return;
        record.observedSwatchTargets.add(target);
        record.swatchImageObserver.observe(target);
      } else prepareCardSwatches(card);
    });
  }

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  const deviceCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

  function deviceSortData(label) {
    const value = String(label || '').trim();
    const normalized = normalize(value);
    const numbers = Array.from(normalized.matchAll(/\d+/g), (match) => Number(match[0]));
    const iphone = normalized.match(/\biphone\s*(\d+)?/);
    if (iphone) {
      const suffixRank = normalized.includes('pro max') ? 0
        : normalized.includes('pro') ? 1
          : normalized.includes('plus') || normalized.includes('air') ? 2
            : /iphone\s*\d+\s*e\b/.test(normalized) ? 4
              : normalized.includes('se') ? 5 : 3;
      return { family: 0, generation: Number(iphone[1] || 0), suffixRank, value };
    }
    const galaxy = normalized.match(/\b(?:galaxy|samsung)(?:\s+[a-z]+)*\s*(\d+)?/);
    if (galaxy) {
      const suffixRank = normalized.includes('ultra') ? 0
        : normalized.includes('plus') || normalized.includes('+') ? 1
          : normalized.includes('pro') ? 2 : 3;
      return { family: 1, generation: Number(galaxy[1] || 0), suffixRank, value };
    }
    if (/\d+\s*mm\b/.test(normalized) && numbers.length) {
      return { family: 2, generation: Math.max(...numbers), suffixRank: 0, value };
    }
    return { family: 3, generation: 0, suffixRank: 0, value };
  }

  function compareDeviceLabels(left, right) {
    const first = deviceSortData(left);
    const second = deviceSortData(right);
    if (first.family !== second.family) return first.family - second.family;
    if (first.generation !== second.generation) return second.generation - first.generation;
    if (first.suffixRank !== second.suffixRank) return first.suffixRank - second.suffixRank;
    return deviceCollator.compare(second.value, first.value);
  }

  function clearPointerFocus(record) {
    record?.pointerFocusedElement?.removeAttribute('data-artbox-pointer-focus');
    if (record) record.pointerFocusedElement = null;
  }

  function focusForInteraction(element, record, pointerActivation = false) {
    if (!element) return;
    clearPointerFocus(record);
    if (record && pointerActivation) {
      element.dataset.artboxPointerFocus = 'true';
      record.pointerFocusedElement = element;
      element.addEventListener('blur', () => {
        element.removeAttribute('data-artbox-pointer-focus');
        if (record.pointerFocusedElement === element) record.pointerFocusedElement = null;
      }, { once: true });
    }
    element.focus();
  }

  function visibleFacetLabel(link) {
    const clone = link.cloneNode(true);
    clone.querySelectorAll('.t4s-value-count, span').forEach((node) => node.remove());
    return clone.textContent.trim();
  }

  function readDevice() {
    try {
      return window.localStorage.getItem(deviceStorageKey);
    } catch (_error) {
      return null;
    }
  }

  function writeDevice(value) {
    try {
      window.localStorage.setItem(deviceStorageKey, value);
    } catch (_error) {
      return;
    }
  }

  function nativeFacets() {
    return Array.from(document.querySelectorAll('#shopify-section-facets [data-filter-links] .t4s-facet'));
  }

  function findNativeFacet(label) {
    const target = normalize(label);
    return nativeFacets().find((facet) => normalize(facet.querySelector('.t4s-facet-title')?.textContent) === target) || null;
  }

  function findNativeFacetLink(label, value) {
    const facet = findNativeFacet(label);
    const target = String(value || '').trim();
    return Array.from(facet?.querySelectorAll('a') || []).find((link) => visibleFacetLabel(link) === target) || null;
  }

  function findNativeHref(href) {
    return Array.from(document.querySelectorAll('#shopify-section-facets [data-filter-links] a'))
      .find((link) => link.getAttribute('href') === href) || null;
  }

  function brandForOption(option) {
    return option?.dataset.deviceBrand || '';
  }

  function setBrand(finder, brand, chooseFirst) {
    const select = finder.querySelector('[data-artbox-device-select]');
    const options = Array.from(select?.options || []);
    finder.querySelectorAll('[data-artbox-device-brand]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.artboxDeviceBrand === brand));
    });
    options.forEach((option) => {
      const matches = brandForOption(option) === brand;
      option.hidden = !matches;
      option.disabled = !matches;
    });
    if (chooseFirst && select && brandForOption(select.selectedOptions[0]) !== brand) {
      const first = options.find((option) => brandForOption(option) === brand && !option.disabled);
      if (first) select.value = first.value;
    }
  }

  function setFinderState(finder, state) {
    finder.dataset.artboxDeviceState = state;
  }

  function syncDevicePresentation(root, mobile) {
    const finder = root.querySelector('[data-artbox-device-finder]');
    if (!finder) return;
    const required = finder.dataset.artboxDeviceState === 'required';
    const expanded = !mobile || required || finder.classList.contains('is-expanded');
    finder.dataset.artboxDeviceMode = mobile ? 'compact' : 'full';
    finder.querySelector('[data-artbox-device-change]')?.setAttribute('aria-expanded', String(expanded));
  }

  function updateDeviceSummary(finder, value) {
    const summary = finder.querySelector('[data-artbox-device-summary]');
    if (!summary) return;
    const template = summary.dataset.deviceSummaryTemplate || '__ARTBOX_DEVICE__';
    summary.textContent = template.replace('__ARTBOX_DEVICE__', value || '');
    summary.hidden = !value;
  }

  function restoreDevice(finder) {
    const select = finder.querySelector('[data-artbox-device-select]');
    const remembered = readDevice();
    if (!select) return;
    const exact = Array.from(select.options).find((option) => option.value === remembered);
    if (!exact) {
      const selected = select.selectedOptions[0];
      setBrand(finder, brandForOption(selected) || 'iphone', false);
      setFinderState(finder, remembered ? 'required' : 'browse-all');
      if (remembered) finder.classList.add('is-expanded');
      return;
    }
    select.value = exact.value;
    setBrand(finder, brandForOption(exact), false);
    updateDeviceSummary(finder, exact.value);
    setFinderState(finder, 'remembered');
    finder.classList.remove('is-expanded');
  }

  function decorateDeviceFinder(root) {
    const finder = root.querySelector('[data-artbox-device-finder]');
    if (!finder || finder.dataset.artboxDeviceReady === 'true') return;
    finder.dataset.artboxDeviceReady = 'true';
    const select = finder.querySelector('[data-artbox-device-select]');
    Array.from(select?.options || [])
      .sort((left, right) => compareDeviceLabels(left.textContent, right.textContent))
      .forEach((option) => select.appendChild(option));
    restoreDevice(finder);
  }

  function readLinkedProducts(card) {
    try {
      const values = JSON.parse(card.querySelector('[data-artbox-linked-products]')?.textContent || '[]');
      return new Map(values.filter((value) => value?.id && value?.url).map((value) => [String(value.id), value]));
    } catch (_error) {
      return new Map();
    }
  }

  function cardFamilyKey(card) {
    return card.dataset.artboxFamilyKey || `product:${card.dataset.artboxSelectedProduct || ''}`;
  }

  function ensureCardProducts(card) {
    let cardRecord = cardRecords.get(card);
    if (!cardRecord) {
      cardRecord = { products: null };
      cardRecords.set(card, cardRecord);
    }
    if (!cardRecord.products) cardRecord.products = readLinkedProducts(card);
    return cardRecord.products;
  }

  function prepareSwatchImage(source) {
    if (preparedSwatchImages.has(source)) return preparedSwatchImages.get(source).ready;
    const image = new Image();
    image.decoding = 'async';
    image.fetchPriority = 'low';
    const ready = new Promise((resolve) => {
      image.onload = async () => {
        try { await image.decode?.(); } catch (_error) { /* Loaded images remain usable after decode rejection. */ }
        resolve();
      };
      image.onerror = () => resolve();
    });
    preparedSwatchImages.set(source, { image, ready });
    image.src = source;
    return ready;
  }

  function prepareCardSwatches(card) {
    const products = ensureCardProducts(card);
    const cardRecord = cardRecords.get(card);
    if (!cardRecord.swatchesPrepared) {
      cardRecord.swatchesPrepared = Promise.all(Array.from(products.values(), (product) => (
        product.swatchImage ? prepareSwatchImage(product.swatchImage) : Promise.resolve()
      )));
    }
    return cardRecord.swatchesPrepared;
  }

  function decorateCards(root, { restoreMemory = false } = {}) {
    const seenFamilies = new Set();
    root.querySelectorAll('[data-artbox-linked-card]').forEach((card) => {
      const familySignature = card.dataset.artboxFamilyKey || cardFamilyKey(card);
      if (familySignature && seenFamilies.has(familySignature)) {
        card.remove();
        return;
      }
      if (familySignature) seenFamilies.add(familySignature);
      if (!cardRecords.has(card)) cardRecords.set(card, { products: null });
      const memory = window.ArtboxColorMemory;
      const group = card.dataset.artboxColorMemoryGroup;
      if (memory && group && (restoreMemory || card.dataset.artboxLinkedCardReady !== 'true')
        && (!card.dataset.artboxCardContext || card.dataset.artboxCardContext === 'collection')) {
        const product = memory.choose(ensureCardProducts(card).values(), memory.read(group, card.dataset.artboxCaseType || ''));
        if (product && String(product.id) !== card.dataset.artboxSelectedProduct) {
          selectLinkedProduct(card, product.id, { remember: false });
        }
      }

      if (card.dataset.artboxLinkedCardReady !== 'true') {
        card.querySelectorAll('[data-artbox-color-chooser-open]').forEach((trigger) => {
          trigger.setAttribute('aria-expanded', 'false');
          trigger.setAttribute('aria-controls', `ArtboxColorChooser-${root.dataset.sectionId || 'collection'}`);
        });
      }
      card.dataset.artboxLinkedCardReady = 'true';
    });
    syncCardDeviceLinks(root);
  }

  function syncCardDeviceLinks(root) {
    if (!root) return;
    const label = root.querySelector('[data-artbox-filter-toolbar]')?.dataset.artboxDeviceFilterLabel || 'Device';
    const facet = findPanelFacet(root, 'device') || findNativeFacet(label);
    const selected = Array.from(facet?.querySelectorAll('li.is--selected a') || []);
    // A filter with several models expresses a range, not one confirmed device.
    const query = new URL(window.location.href).searchParams;
    const models = [...query.getAll('filter.v.option.device'), ...query.getAll('filter.p.option.device')]
      .flatMap(value => value.split(',')).map(value => value.trim()).filter(Boolean);
    const model = facet ? (selected.length === 1 ? visibleFacetLabel(selected[0]) : '')
      : models.length === 1 ? models[0] : '';
    root.querySelectorAll('[data-artbox-card-link], [data-artbox-quick-shop-trigger]').forEach((link) => {
      try {
        const url = new URL(link.getAttribute('href'), window.location.href);
        if (url.origin !== window.location.origin || !url.pathname.includes('/products/')) return;
        if (model) url.searchParams.set('artbox_device', model);
        else url.searchParams.delete('artbox_device');
        const card = link.closest('[data-artbox-linked-card]');
        if (card?.dataset.artboxCaseType || card?.dataset.artboxCardContext === 'search') {
          if (model) url.searchParams.delete('artbox_choose_device');
          else url.searchParams.set('artbox_choose_device', '1');
          card.dataset.artboxDevice = model;
          card.dataset.artboxDeviceConfirmed = String(Boolean(model));
          if (link.matches('[data-artbox-quick-shop-trigger]')) {
            link.dataset.artboxDevice = model;
            link.dataset.artboxDeviceConfirmed = String(Boolean(model));
          }
        }
        const href = `${url.pathname}${url.search}${url.hash}`;
        if (link.getAttribute('href') !== href) link.setAttribute('href', href);
      } catch (_error) {
        // A malformed card URL must not interrupt the collection controls.
      }
    });
  }

  function replacePrice(card, product) {
    const container = card.querySelector('[data-artbox-card-price]');
    if (!container) return;
    let priceRoot = container.querySelector('.t4s-product-price');
    if (!priceRoot) {
      priceRoot = document.createElement('div');
      priceRoot.className = 't4s-product-price';
      priceRoot.dataset.prPrice = '';
      container.replaceChildren(priceRoot);
    }
    let compare = priceRoot.querySelector('[data-artbox-card-compare]');
    let current = priceRoot.querySelector('[data-artbox-card-current]');
    const currentTag = product.compareAt ? 'INS' : 'SPAN';
    if (!compare || !current || current.tagName !== currentTag) {
      compare = document.createElement('del');
      compare.dataset.artboxCardCompare = '';
      current = document.createElement(product.compareAt ? 'ins' : 'span');
      current.dataset.artboxCardCurrent = '';
      priceRoot.replaceChildren(compare, current);
    }
    compare.textContent = product.compareAt || '';
    compare.hidden = !product.compareAt;
    current.textContent = product.price || '';
  }

  function updateMerchandisingLabel(card, product) {
    let label = card.querySelector('[data-artbox-merchandising-label]');
    if (!label) {
      label = document.createElement('span');
      label.className = 'artbox-collection-card__label';
      label.dataset.artboxMerchandisingLabel = '';
      card.querySelector('[data-artbox-card-label-slot]')?.appendChild(label);
    }
    label.textContent = product.label || '';
    label.dataset.labelKind = product.labelKind || '';
    label.hidden = !product.label;
  }

  function selectLinkedProduct(card, productId, { remember = true } = {}) {
    const product = ensureCardProducts(card).get(String(productId));
    if (!product) return false;

    card.querySelectorAll('[data-artbox-card-link]').forEach((link) => {
      link.setAttribute('href', product.url);
      if (link.hasAttribute('aria-label')) link.setAttribute('aria-label', product.title || '');
    });
    const image = card.querySelector('[data-artbox-card-image]');
    if (image && product.image720) {
      image.setAttribute('src', product.image720);
      image.setAttribute('srcset', [product.image360 && `${product.image360} 360w`, `${product.image720} 720w`].filter(Boolean).join(', '));
      image.setAttribute('alt', product.imageAlt || product.title || '');
    }
    const title = card.querySelector('[data-artbox-card-title]');
    if (title) title.textContent = product.displayTitle || product.title || '';
    const color = card.querySelector('[data-artbox-card-color]');
    if (color) {
      color.textContent = product.colorName || '';
      color.hidden = !product.colorName;
    }
    replacePrice(card, product);
    updateMerchandisingLabel(card, product);

    const quickShop = card.querySelector('[data-artbox-quick-shop-trigger]');
    if (quickShop) {
      const quickShopLabel = quickShop.dataset.quickShopLabel || quickShop.getAttribute('aria-label')?.split(':')[0] || '';
      quickShop.dataset.quickShopLabel = quickShopLabel;
      quickShop.dataset.id = String(product.quickShopId || product.id);
      if (typeof window.jQuery_T4NT === 'function') {
        window.jQuery_T4NT(quickShop).data('id', String(product.quickShopId || product.id));
      }
      quickShop.setAttribute('href', product.url);
      quickShop.setAttribute('aria-label', `${quickShopLabel}: ${product.title}`);
    }

    card.dataset.artboxSelectedProduct = String(product.id);
    card.dataset.artboxAvailable = String(Boolean(product.available));
    card.classList.toggle('is-sold-out', !product.available);
    card.querySelectorAll('[data-artbox-linked-color]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.artboxLinkedColor === String(product.id)));
    });
    if (remember && card.dataset.artboxColorMemoryGroup
      && (!card.dataset.artboxCardContext || card.dataset.artboxCardContext === 'collection')) {
      window.ArtboxColorMemory?.write(card.dataset.artboxColorMemoryGroup, card.dataset.artboxCaseType || '', product.memoryId || product.id);
    }
    syncCardDeviceLinks(card.closest('[data-artbox-collection]'));
    return true;
  }

  function ensureSharedDialog(root, record) {
    if (record.sharedDialog?.isConnected) return record.sharedDialog;
    const existing = root.querySelector('[data-artbox-shared-color-dialog]');
    if (existing) {
      record.sharedDialog = existing;
      return existing;
    }
    const dialog = document.createElement('div');
    dialog.id = `ArtboxColorChooser-${root.dataset.sectionId || 'collection'}`;
    dialog.className = 'artbox-linked-colors__shared-dialog';
    dialog.dataset.artboxSharedColorDialog = '';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.hidden = true;
    root.appendChild(dialog);
    record.sharedDialog = dialog;
    return dialog;
  }

  function closeSharedDialog(record, restoreFocus = true, pointerActivation = false) {
    record.dialogSequence = (record.dialogSequence || 0) + 1;
    record.dialogPendingInvoker?.removeAttribute('aria-busy');
    record.dialogPendingInvoker = null;
    const dialog = record.sharedDialog;
    if (!dialog || dialog.hidden) return;
    dialog.hidden = true;
    dialog.replaceChildren();
    record.dialogInvoker?.setAttribute('aria-expanded', 'false');
    if (restoreFocus && record.dialogInvoker?.isConnected) {
      focusForInteraction(record.dialogInvoker, record, pointerActivation);
    }
    record.dialogCard = null;
    record.dialogInvoker = null;
  }

  async function openSharedDialog(root, record, card, invoker, pointerActivation = false) {
    const products = ensureCardProducts(card);
    if (!products.size) return;
    closeSharedDialog(record, false);
    const sequence = record.dialogSequence;
    record.dialogPendingInvoker = invoker;
    invoker.setAttribute('aria-busy', 'true');
    await prepareCardSwatches(card);
    if (record.dialogSequence !== sequence || !root.isConnected || !card.isConnected) return;
    invoker.removeAttribute('aria-busy');
    record.dialogPendingInvoker = null;
    const dialog = ensureSharedDialog(root, record);
    const selectedId = String(card.dataset.artboxSelectedProduct || '');
    const selectedProduct = products.get(selectedId);

    const header = document.createElement('div');
    header.className = 'artbox-linked-colors__dialog-header';
    const heading = document.createElement('h4');
    heading.id = `${dialog.id}-Title`;
    heading.dataset.artboxColorDialogTitle = '';
    heading.textContent = selectedProduct?.title || '';
    const close = document.createElement('button');
    close.type = 'button';
    close.dataset.artboxColorDialogClose = '';
    close.setAttribute('aria-label', card.querySelector('[data-artbox-linked-colors]')?.dataset.artboxChooserCloseLabel || 'Close');
    close.textContent = '×';
    header.append(heading, close);

    const options = document.createElement('div');
    options.className = 'artbox-linked-colors__dialog-options';
    products.forEach((product) => {
      const option = document.createElement('button');
      option.type = 'button';
      option.className = 'artbox-linked-colors__dialog-option';
      option.dataset.artboxLinkedColor = String(product.id);
      option.setAttribute('aria-pressed', String(String(product.id) === selectedId));
      if (!product.available) option.dataset.soldOut = 'true';

      const swatch = document.createElement('span');
      swatch.className = `artbox-linked-colors__dialog-swatch ${product.swatchClass || ''}`.trim();
      if (product.swatchImage) swatch.style.setProperty('--artbox-swatch-image', `url("${product.swatchImage}")`);
      else if (product.swatchColor) swatch.style.setProperty('--artbox-swatch-color', product.swatchColor);

      const label = document.createElement('span');
      label.textContent = product.colorName || product.title || '';
      option.append(swatch, label);
      options.append(option);
    });

    const surface = document.createElement('div');
    surface.className = 'artbox-linked-colors__dialog-surface';
    surface.append(header, options);
    dialog.replaceChildren(surface);
    dialog.setAttribute('aria-labelledby', heading.id);
    dialog.hidden = false;
    record.dialogCard = card;
    record.dialogInvoker = invoker;
    invoker.setAttribute('aria-expanded', 'true');
    focusForInteraction(
      dialog.querySelector('[data-artbox-linked-color]') || dialog.querySelector('[data-artbox-color-dialog-close], button, a'),
      record,
      pointerActivation,
    );
  }

  function trapDialogFocus(event, record) {
    const dialog = record.sharedDialog;
    if (event.key === 'Escape' && dialog && !dialog.hidden) {
      event.preventDefault();
      closeSharedDialog(record);
      return;
    }
    if (event.key !== 'Tab' || !dialog || dialog.hidden) return;
    const focusable = Array.from(dialog.querySelectorAll('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'))
      .filter((element) => !element.hidden);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function ensureFilterPanelChrome(panel) {
    if (!panel) return;
    const root = panel.closest('[data-artbox-collection]');
    let header = panel.querySelector('[data-artbox-filter-sheet-header]');
    if (!header) {
      header = document.createElement('div');
      header.className = 'artbox-collection__sheet-header';
      header.dataset.artboxFilterSheetHeader = '';
      panel.prepend(header);
    }
    let close = header.querySelector('[data-artbox-filter-sheet-close]');
    if (!close) {
      close = document.createElement('button');
      close.type = 'button';
      close.className = 'artbox-collection__sheet-close';
      close.dataset.artboxFilterSheetClose = '';
      close.setAttribute('aria-label', panel.dataset.artboxFilterCloseLabel || '');
      close.textContent = '×';
      header.appendChild(close);
    }
    let reset = header.querySelector('[data-artbox-filter-reset]');
    if (!reset) {
      const current = new URL(window.location.href);
      const sortBy = current.searchParams.get('sort_by');
      const searchTerm = root?.hasAttribute('data-artbox-search') ? current.searchParams.get('q') : null;
      const searchType = searchTerm ? current.searchParams.get('type') : null;
      current.search = '';
      if (searchTerm) current.searchParams.set('q', searchTerm);
      if (searchType) current.searchParams.set('type', searchType);
      if (sortBy) current.searchParams.set('sort_by', sortBy);
      const toolbarReset = root?.querySelector('.artbox-collection__reset');
      reset = document.createElement('a');
      reset.className = 'artbox-collection__sheet-reset';
      reset.dataset.artboxFilterReset = '';
      reset.href = toolbarReset?.href || `${current.pathname}${current.search}`;
      reset.textContent = root?.querySelector('[data-artbox-filter-toolbar]')?.dataset.artboxResetLabel || 'Reset';
      header.appendChild(reset);
    }
  }

  function facetKey(facet) {
    return facet?.dataset.artboxFacetKey || normalize(facet?.querySelector('.t4s-facet-title')?.textContent);
  }

  function supportedFacetDefinitions(root) {
    const toolbar = root.querySelector('[data-artbox-filter-toolbar]');
    const deviceLabel = toolbar?.dataset.artboxDeviceFilterLabel
      || root.querySelector('[data-artbox-device-finder]')?.dataset.deviceFilterLabel
      || 'device';
    return [
      { key: 'availability', match: 'availability', label: 'Availability' },
      {
        key: 'category',
        match: normalize(toolbar?.dataset.artboxCategoryFilterLabel || 'Product type'),
        label: toolbar?.dataset.artboxCategoryLabel || 'Category',
      },
      {
        key: 'brand',
        match: normalize(toolbar?.dataset.artboxBrandFilterLabel || 'Compatible Brand'),
        label: toolbar?.dataset.artboxBrandLabel || 'Compatible Brand',
      },
      { key: 'device', match: normalize(deviceLabel), label: 'Device' },
    ];
  }

  function retainSupportedFacets(root, facets) {
    const definitions = supportedFacetDefinitions(root);
    const retained = facets.filter((facet) => {
      const nativeKey = facet.dataset.artboxFacetNativeKey
        || normalize(facet.querySelector('.t4s-facet-title')?.textContent);
      const definition = definitions.find((candidate) => candidate.match === nativeKey);
      const keep = Boolean(definition) && !facet.classList.contains('is--blockid_price');
      if (definition) {
        facet.dataset.artboxFacetNativeKey = nativeKey;
        facet.dataset.artboxFacetKey = definition.key;
        facet.dataset.artboxFacetLabel = definition.label;
      }
      if (!keep && facet.closest('[data-artbox-filter-panel]')) facet.remove();
      return keep;
    });
    retained.sort((left, right) => (
      definitions.findIndex((definition) => definition.key === facetKey(left))
      - definitions.findIndex((definition) => definition.key === facetKey(right))
    ));
    const parent = retained[0]?.parentElement;
    const currentOrder = Array.from(parent?.children || []).filter((child) => retained.includes(child));
    if (currentOrder.some((facet, index) => facet !== retained[index])) {
      retained.forEach((facet) => parent.appendChild(facet));
    }
    return retained;
  }

  function panelFacets(root) {
    const panel = root.querySelector('[data-artbox-filter-panel]');
    let local = Array.from(panel?.querySelectorAll('.t4s-facet') || []);
    if (!local.length && panel && panel.dataset.artboxFacetsLoaded !== 'true') {
      const nativeForm = document.querySelector('#shopify-section-facets [data-filter-links] #FacetFiltersForm');
      if (nativeForm) {
        panel.replaceChildren(nativeForm.cloneNode(true));
        panel.dataset.artboxFacetsLoaded = 'true';
        local = Array.from(panel.querySelectorAll('.t4s-facet'));
      }
    }
    ensureFilterPanelChrome(panel);
    return retainSupportedFacets(root, local.length ? local : nativeFacets());
  }

  async function ensurePanelFacets(root) {
    const panel = root.querySelector('[data-artbox-filter-panel]');
    if (!panel) return [];
    const existing = panelFacets(root);
    if (existing.length && panel.querySelector('.t4s-facet')) return existing;
    if (panel.dataset.artboxFacetsLoaded === 'true') return existing;
    if (filterRequests.has(panel)) return filterRequests.get(panel);
    const optionsNode = root.querySelector('[data-ntajax-options]');
    let sectionId = panel.dataset.artboxFilterSection;
    if (!sectionId && optionsNode) {
      try {
        sectionId = JSON.parse(optionsNode.getAttribute('data-ntajax-options') || '{}').typeFilters;
      } catch (_error) {
        sectionId = '';
      }
    }
    if (!sectionId) return [];

    const request = (async () => {
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('page');
        url.searchParams.set('section_id', sectionId);
        const response = await fetch(url, { headers: { 'Cache-Control': 'no-cache' } });
        if (!response.ok) return [];
        const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
        const form = documentFragment.querySelector('[data-filter-links] #FacetFiltersForm');
        if (!form) return [];
        panel.replaceChildren(form.cloneNode(true));
        panel.dataset.artboxFacetsLoaded = 'true';
        ensureFilterPanelChrome(panel);
        return retainSupportedFacets(root, Array.from(panel.querySelectorAll('.t4s-facet')));
      } catch (_error) {
        return [];
      } finally {
        filterRequests.delete(panel);
      }
    })();
    filterRequests.set(panel, request);
    return request;
  }

  function findPanelFacet(root, label) {
    const target = normalize(label);
    return Array.from(root.querySelectorAll('[data-artbox-filter-panel] .t4s-facet'))
      .find((facet) => facetKey(facet) === target) || null;
  }

  function activateFilterLink(root, link) {
    if (!link) return false;
    const facet = link.closest('.t4s-facet');
    if (facetKey(facet) === 'device') {
      const selected = Array.from(facet.querySelectorAll('li.is--selected a'));
      const next = link.closest('li')?.classList.contains('is--selected')
        ? selected.filter((candidate) => candidate !== link) : [...selected, link];
      if (next.length === 1) writeDevice(visibleFacetLabel(next[0]));
    }
    const facets = root.querySelector('[data-ntajax-options]')?.Facets;
    if (facets?.filterActionLink && window.jQuery_T4NT) {
      facets.filterActionLink(window.jQuery_T4NT(link));
      return true;
    }
    const nativeLink = findNativeHref(link.getAttribute('href'));
    if (!nativeLink) return false;
    nativeLink.click();
    return true;
  }

  function facetActiveCount(facet) {
    return facet.querySelectorAll('li.is--selected').length;
  }

  function decorateDeviceFacet(root, facet) {
    if (facetKey(facet) !== 'device') return;
    if (facet.dataset.artboxDeviceFacetReady === 'true') return;
    const values = facet.querySelector('.t4s-filter__values');
    if (!values) return;
    const items = Array.from(values.querySelectorAll(':scope > li'));
    const sortedItems = [...items].sort((left, right) => compareDeviceLabels(
      visibleFacetLabel(left.querySelector('a') || left),
      visibleFacetLabel(right.querySelector('a') || right),
    ));
    if (items.some((item, index) => item !== sortedItems[index])) {
      sortedItems.forEach((item) => values.appendChild(item));
    }
    values.dataset.artboxDeviceValues = '';
    delete values.dataset.artboxDeviceExpanded;
    sortedItems.forEach((item, index) => {
      if (index >= 9 && !item.classList.contains('is--selected')) item.dataset.artboxDeviceOverflow = '';
      else delete item.dataset.artboxDeviceOverflow;
    });

    let more = facet.querySelector('[data-artbox-device-more]');
    if (!more) {
      more = document.createElement('button');
      more.type = 'button';
      more.className = 'artbox-collection__device-more';
      more.dataset.artboxDeviceMore = '';
      facet.appendChild(more);
    }
    more.textContent = root.querySelector('[data-artbox-filter-toolbar]')?.dataset.artboxMoreLabel || 'More';
    more.setAttribute('aria-expanded', 'false');
    more.hidden = !items.some((item) => item.hasAttribute('data-artbox-device-overflow'));

    let help = facet.querySelector('[data-artbox-model-help]');
    if (!help) {
      help = document.createElement('div');
      help.className = 'artbox-collection__model-help';
      help.dataset.artboxModelHelp = '';
      facet.appendChild(help);
    }
    const toolbar = root.querySelector('[data-artbox-filter-toolbar]');
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('fill', 'none');
    icon.setAttribute('stroke', 'currentColor');
    icon.setAttribute('stroke-width', '1.5');
    const gear = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    gear.setAttribute('d', 'M10 2h4l.5 2.1 1.7.7 1.8-1.1 2.8 2.8-1.1 1.8.7 1.7L22.5 10v4l-2.1.5-.7 1.7 1.1 1.8-2.8 2.8-1.8-1.1-1.7.7-.5 2.1h-4l-.5-2.1-1.7-.7-1.8 1.1-2.8-2.8 1.1-1.8-.7-1.7L1.5 14v-4l2.1-.5.7-1.7-1.1-1.8L6 3.2l1.8 1.1 1.7-.7L10 2Z');
    const center = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    center.setAttribute('cx', '12');
    center.setAttribute('cy', '12');
    center.setAttribute('r', '4');
    icon.append(gear, center);
    const iphoneIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    iphoneIcon.setAttribute('viewBox', '0 0 24 24');
    iphoneIcon.setAttribute('aria-hidden', 'true');
    iphoneIcon.setAttribute('fill', 'none');
    iphoneIcon.setAttribute('stroke', 'currentColor');
    iphoneIcon.setAttribute('stroke-width', '1.4');
    iphoneIcon.setAttribute('stroke-linejoin', 'round');
    const teeth = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
    teeth.setAttribute('points', Array.from({ length: 48 }, (_, index) => {
      const angle = (index / 48) * Math.PI * 2 - Math.PI / 2;
      const radius = [9.2, 10.6, 10.6, 9.2][index % 4];
      return `${(12 + Math.cos(angle) * radius).toFixed(2)},${(12 + Math.sin(angle) * radius).toFixed(2)}`;
    }).join(' '));
    const iphoneCenter = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    iphoneCenter.setAttribute('cx', '12');
    iphoneCenter.setAttribute('cy', '12');
    iphoneCenter.setAttribute('r', '4.7');
    const pointer = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    pointer.setAttribute('d', 'M11 9.5 14.2 12 11 14.5Z');
    pointer.setAttribute('fill', 'currentColor');
    pointer.setAttribute('stroke', 'none');
    iphoneIcon.append(teeth, iphoneCenter, pointer);
    const copy = document.createElement('div');
    const title = document.createElement('strong');
    title.textContent = toolbar?.dataset.artboxFindModelTitle || 'Find your model';
    const iphone = document.createElement('p');
    iphone.textContent = toolbar?.dataset.artboxFindIphoneModel || '';
    const samsung = document.createElement('p');
    samsung.textContent = toolbar?.dataset.artboxFindSamsungModel || '';
    copy.append(title, iphone);
    const iphoneRow = document.createElement('div');
    iphoneRow.className = 'artbox-collection__model-platform';
    iphoneRow.dataset.artboxModelPlatform = 'iphone';
    iphoneRow.append(iphoneIcon, copy);
    const samsungRow = document.createElement('div');
    samsungRow.className = 'artbox-collection__model-platform';
    samsungRow.dataset.artboxModelPlatform = 'samsung';
    samsungRow.append(icon, samsung);
    help.replaceChildren(iphoneRow, samsungRow);
    facet.dataset.artboxDeviceFacetReady = 'true';
  }

  function setFacetAccordion(root, key) {
    root.dataset.artboxAccordionFacet = key;
    panelFacets(root).forEach((facet) => {
      const expanded = facetKey(facet) === key;
      facet.hidden = false;
      facet.querySelector('[data-artbox-facet-accordion]')?.setAttribute('aria-expanded', String(expanded));
      const content = facet.querySelector('[data-artbox-facet-content]');
      if (content) content.hidden = !expanded;
    });
  }

  function toggleFacetAccordion(root, button) {
    const facet = button.closest('.t4s-facet');
    const content = facet?.querySelector(':scope > [data-artbox-facet-content]');
    if (!content) return;
    const expanded = button.getAttribute('aria-expanded') === 'true';
    button.setAttribute('aria-expanded', String(!expanded));
    content.hidden = expanded;
    resizeFilterSheet(root, { animate: true });
  }

  function syncFacetAccordionMode(root) {
    const mobile = records.get(root)?.filterMode === 'sheet';
    root.querySelectorAll('[data-artbox-facet-accordion]').forEach((button) => {
      const content = button.closest('.t4s-facet')?.querySelector(':scope > [data-artbox-facet-content]');
      if (mobile) {
        button.removeAttribute('tabindex');
        if (content?.id) button.setAttribute('aria-controls', content.id);
        return;
      }
      button.tabIndex = -1;
      button.removeAttribute('aria-expanded');
      button.removeAttribute('aria-controls');
    });
  }

  function decorateFacetAccordions(root, facets) {
    facets.forEach((facet) => {
      decorateDeviceFacet(root, facet);
      const title = facet.querySelector('.t4s-facet-title');
      if (!title) return;
      const key = facetKey(facet);
      let button = title.querySelector('[data-artbox-facet-accordion]');
      if (!button) {
        const label = facet.dataset.artboxFacetLabel || title.textContent.trim();
        button = document.createElement('button');
        button.type = 'button';
        button.dataset.artboxFacetAccordion = key;
        button.textContent = label;
        title.replaceChildren(button);
      }
      let content = facet.querySelector(':scope > [data-artbox-facet-content]');
      if (!content) {
        content = document.createElement('div');
        content.dataset.artboxFacetContent = '';
        while (title.nextSibling) content.appendChild(title.nextSibling);
        facet.appendChild(content);
      }
      if (!content.id) {
        facetSequence += 1;
        content.id = `ArtboxFacetPanel-${facetSequence}`;
      }
    });
    const accordionsInitialised = facets.every((facet) => (
      facet.querySelector('[data-artbox-facet-accordion]')?.hasAttribute('aria-expanded')
    ));
    if (!accordionsInitialised) {
      const active = facets.find((facet) => facetActiveCount(facet) > 0);
      const requested = root.dataset.artboxAccordionFacet;
      const requestedExists = root.hasAttribute('data-artbox-accordion-facet')
        && (requested === '' || facets.some((facet) => facetKey(facet) === requested));
      setFacetAccordion(root, requestedExists ? requested : facetKey(active || facets[0]));
    }
    syncFacetAccordionMode(root);
  }

  function selectFacetTab(root, key) {
    const panel = root.querySelector('[data-artbox-filter-panel]');
    const facets = Array.from(panel?.querySelectorAll('.t4s-facet') || []);
    root.dataset.artboxActiveFacet = key;
    root.querySelectorAll('[data-artbox-facet-tab]').forEach((button) => {
      const selected = button.dataset.artboxFacetTab === key;
      button.setAttribute('aria-selected', String(selected));
      button.classList.toggle('is-selected', selected);
    });
    const mobile = records.get(root)?.filterMode === 'sheet';
    facets.forEach((facet) => {
      if (mobile) {
        facet.hidden = false;
        return;
      }
      facet.hidden = facetKey(facet) !== key;
      const content = facet.querySelector('[data-artbox-facet-content]');
      if (content) content.hidden = false;
    });
  }

  function decorateFacetTabs(root) {
    const tabs = root.querySelector('[data-artbox-facet-tabs]');
    if (!tabs) return;
    const facets = panelFacets(root);
    const panel = root.querySelector('[data-artbox-filter-panel]');
    const trigger = root.querySelector('[data-artbox-filter-toolbar] .t4s-btn-filter');
    if (!facets.length) {
      if (tabs.dataset.artboxFacetSsr === 'true' && tabs.querySelector('[data-artbox-facet-tab]')
        && panel && panel.dataset.artboxFacetsLoaded !== 'true') {
        root.dataset.artboxCoreFacetsReady = 'true';
        return;
      }
      delete root.dataset.artboxCoreFacetsReady;
      delete tabs.dataset.artboxFacetSsr;
      delete tabs.dataset.artboxFacetSignature;
      tabs.replaceChildren();
      if (trigger) trigger.hidden = panel?.dataset.artboxFacetsLoaded === 'true';
      return;
    }
    delete tabs.dataset.artboxFacetSsr;
    root.dataset.artboxCoreFacetsReady = 'true';
    if (trigger) trigger.hidden = false;
    decorateFacetAccordions(root, facets);
    const labels = facets.map((facet) => facet.dataset.artboxFacetLabel || facet.querySelector('.t4s-facet-title')?.textContent?.trim()).filter(Boolean);
    const signature = facets.map((facet) => `${facetKey(facet)}:${facetActiveCount(facet)}`).join('|');
    const requestedKey = root.dataset.artboxActiveFacet;
    const selectedKey = facets.some((facet) => facetKey(facet) === requestedKey)
      ? requestedKey
      : facetKey(facets[0]);
    if (tabs.dataset.artboxFacetSignature === signature) {
      if (records.get(root)?.filterMode !== 'sheet') {
        selectFacetTab(root, selectedKey);
      }
      return;
    }
    tabs.dataset.artboxFacetSignature = signature;
    tabs.replaceChildren();
    tabs.setAttribute('role', 'tablist');
    labels.forEach((label, index) => {
      const button = document.createElement('button');
      const key = facetKey(facets[index]);
      facetSequence += 1;
      button.type = 'button';
      button.id = `ArtboxFacetTab-${facetSequence}`;
      button.dataset.artboxFacetTab = key;
      button.setAttribute('role', 'tab');
      button.setAttribute('aria-selected', String(index === 0));
      button.setAttribute('aria-expanded', 'false');
      const panel = root.querySelector('[data-artbox-filter-panel]');
      if (panel?.id) button.setAttribute('aria-controls', panel.id);
      button.append(document.createTextNode(label));
      const count = facetActiveCount(facets[index]);
      if (count > 0) {
        const badge = document.createElement('span');
        badge.className = 'artbox-collection__active-count';
        badge.textContent = String(count);
        button.appendChild(badge);
      }
      tabs.appendChild(button);
    });
    selectFacetTab(root, selectedKey);
  }

  function activeHeader() {
    return document.querySelector('.t4s-section-header');
  }

  function renderedHeaderBottom(header = activeHeader()) {
    if (!header) return 0;
    const rect = header.getBoundingClientRect();
    return Math.max(0, rect.bottom);
  }

  function syncHeaderMotionState(root, header) {
    const height = header ? Math.max(0, header.getBoundingClientRect().height) : 0;
    const hidden = Boolean(header?.classList.contains('shopify-section-header-hidden'));
    root.style.setProperty('--artbox-header-height', `${height}px`);
    root.dataset.artboxHeaderHidden = String(hidden);
    return { height, hidden };
  }

  function clearStickyMotion(record) {
    if (record.headerMotionFrame) cancelAnimationFrame(record.headerMotionFrame);
    record.headerMotionFrame = null;
    record.headerMotionActive = false;
  }

  function setHeaderOffset(root, record, next) {
    const toolbar = root.querySelector('[data-artbox-filter-toolbar]');
    if (!toolbar) return;
    const offset = Math.max(0, next || 0);
    if (Math.abs((record.headerOffset || 0) - offset) <= 0.01
      && toolbar.style.getPropertyValue('--artbox-header-offset')) return;
    record.headerOffset = offset;
    toolbar.style.setProperty('--artbox-header-offset', `${offset}px`);
  }

  function transitionTimeMs(value) {
    const parsed = Number.parseFloat(value);
    if (!Number.isFinite(parsed)) return 0;
    return value.trim().endsWith('ms') ? parsed : parsed * 1000;
  }

  function headerTransformMotion(header) {
    const fallback = { delayMs: 0, durationMs: 0, timingFunction: 'ease' };
    if (!header) return fallback;
    const style = getComputedStyle(header);
    const properties = style.transitionProperty.split(',').map((value) => value.trim());
    const durations = style.transitionDuration.split(',').map(transitionTimeMs);
    const delays = style.transitionDelay.split(',').map(transitionTimeMs);
    const timingFunctions = style.transitionTimingFunction.split(',').map((value) => value.trim());
    return properties.reduce((longest, property, index) => {
      if (property !== 'transform' && property !== 'all') return longest;
      const durationMs = durations[index % durations.length] || 0;
      const delayMs = delays[index % delays.length] || 0;
      if (durationMs + delayMs <= longest.durationMs + longest.delayMs) return longest;
      return {
        delayMs,
        durationMs,
        timingFunction: timingFunctions[index % timingFunctions.length] || 'ease',
      };
    }, fallback);
  }

  function runningHeaderTransformMotion(header) {
    if (!header || typeof header.getAnimations !== 'function') return null;
    const animation = header.getAnimations().find((candidate) => (
      candidate.transitionProperty === 'transform'
      && (candidate.playState === 'running' || candidate.pending)
    ));
    if (!animation?.effect) return null;
    const timing = animation.effect.getTiming();
    const computed = animation.effect.getComputedTiming();
    const durationMs = Number(computed.duration);
    if (!Number.isFinite(durationMs) || durationMs <= 0) return null;
    const localTime = Number(computed.localTime) || 0;
    const endTime = Number(computed.endTime);
    return {
      delayMs: 0,
      durationMs: Number.isFinite(endTime) ? Math.max(0, endTime - localTime) : durationMs,
      timingFunction: timing.easing || 'ease',
    };
  }

  function setHeaderMotion(root, durationMs = 0, delayMs = 0, timingFunction = 'ease') {
    const toolbar = root.querySelector('[data-artbox-filter-toolbar]');
    if (!toolbar) return;
    toolbar.style.setProperty('--artbox-header-motion-duration', `${Math.max(0, durationMs)}ms`);
    toolbar.style.setProperty('--artbox-header-motion-delay', `${Math.max(0, delayMs)}ms`);
    toolbar.style.setProperty('--artbox-header-motion-timing', timingFunction || 'ease');
  }

  function commitRenderedHeaderTarget(root, record, header, headerState, motionOverride = null) {
    clearStickyMotion(record);
    const currentScroll = window.scrollY;
    const scrollDelta = currentScroll - record.lastScrollY;
    const scrollingDown = scrollDelta > 1 || (Math.abs(scrollDelta) <= 1 && record.scrollDirection === 'down');
    if (!headerState.hidden && renderedHeaderBottom(header) <= 0 && scrollingDown && !record.headerIntentPending) {
      setHeaderMotion(root);
      setHeaderOffset(root, record, 0);
      return;
    }
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const measuredMotion = headerTransformMotion(header);
    const motion = reducedMotion
      ? { delayMs: 0, durationMs: 0, timingFunction: 'ease' }
      : motionOverride || measuredMotion;
    if (!reducedMotion && motionOverride && motion.durationMs + motion.delayMs > 0) {
      const toolbar = root.querySelector('[data-artbox-filter-toolbar]');
      setHeaderMotion(root);
      setHeaderOffset(root, record, renderedHeaderBottom(header));
      if (toolbar) getComputedStyle(toolbar).getPropertyValue('--artbox-header-offset');
    }
    record.headerMotionActive = motion.durationMs + motion.delayMs > 0;
    setHeaderMotion(root, motion.durationMs, motion.delayMs, motion.timingFunction);
    setHeaderOffset(root, record, headerState.hidden ? 0 : headerState.height);
  }

  function applyStickyTop(root, record, next) {
    root.style.setProperty('--artbox-sticky-top', `${Math.max(0, next)}px`);
    record.stickyTop = next;
    createStickyObserver(root, record);
    recalibrateStickyState(root, record);
  }

  function refreshStickyThreshold(root, record) {
    const sentinel = root.querySelector('[data-artbox-filter-sentinel]');
    if (!sentinel) return;
    record.stickySentinel = sentinel;
    record.stickyThreshold = sentinel.getBoundingClientRect().bottom
      + window.scrollY
      - Math.max(0, record.stickyTop || 0);
  }

  function syncStickyState(root, record) {
    const sentinel = root.querySelector('[data-artbox-filter-sentinel]');
    const shell = root.querySelector('[data-artbox-filter-shell]');
    if (!sentinel || !shell) return;
    if (record.stickySentinel !== sentinel || !Number.isFinite(record.stickyThreshold)) {
      refreshStickyThreshold(root, record);
    }
    const isSticky = window.scrollY >= record.stickyThreshold;
    const next = String(isSticky);
    if (shell.dataset.artboxSticky !== next) shell.dataset.artboxSticky = next;
  }

  function recalibrateStickyState(root, record) {
    refreshStickyThreshold(root, record);
    syncStickyState(root, record);
  }

  function createStickyObserver(root, record) {
    const sentinel = root.querySelector('[data-artbox-filter-sentinel]');
    const shell = root.querySelector('[data-artbox-filter-shell]');
    if (!sentinel || !shell) return;
    record.stickyObserver?.disconnect();
    record.stickySentinel = sentinel;
    record.stickyObserver = new IntersectionObserver(([entry]) => {
      const stickyTop = Number.parseFloat(getComputedStyle(root).getPropertyValue('--artbox-sticky-top')) || 0;
      shell.dataset.artboxSticky = String(!entry.isIntersecting && entry.boundingClientRect.top <= stickyTop);
    }, { threshold: 0, rootMargin: `-${record.stickyTop}px 0px 0px 0px` });
    record.stickyObserver.observe(sentinel);
  }

  function unbindObservedHeader(record) {
    record.headerObserver?.disconnect();
    record.headerStateObserver?.disconnect();
    record.headerIntentToken += 1;
    record.headerIntentPending = false;
    record.headerIntentHidden = null;
    record.observedHeader?.removeEventListener('T4sHeaderHide', record.handleHeaderIntent);
    record.observedHeader?.removeEventListener('T4sHeaderReveal', record.handleHeaderIntent);
    record.observedHeader?.removeEventListener('transitionend', record.handleHeaderTransition);
    record.observedHeader?.removeEventListener('transitioncancel', record.handleHeaderTransition);
    record.headerObserver = null;
    record.headerStateObserver = null;
    record.handleHeaderIntent = null;
    record.handleHeaderTransition = null;
    record.observedHeader = null;
  }

  function bindObservedHeader(root, record, header = activeHeader()) {
    if (record.observedHeader === header) return header;
    clearStickyMotion(record);
    unbindObservedHeader(record);
    if (!header) return null;
    record.observedHeader = header;
    record.headerIntentHidden = header.classList.contains('shopify-section-header-hidden');
    if ('ResizeObserver' in window) {
      record.headerObserver = new ResizeObserver(() => measureStickyTop(root, record));
      record.headerObserver.observe(header);
    }
    if ('MutationObserver' in window) {
      record.headerStateObserver = new MutationObserver(() => {
        const hidden = header.classList.contains('shopify-section-header-hidden');
        const directionChanged = record.headerIntentHidden !== hidden;
        if (!record.headerIntentPending) record.headerIntentHidden = hidden;
        measureStickyTop(root, record, !record.headerIntentPending && directionChanged ? { trackMotion: true } : {});
      });
      record.headerStateObserver.observe(header, { attributes: true, attributeFilter: ['class', 'style'] });
    }
    record.handleHeaderIntent = (event) => {
      if (!record.mobileQuery?.matches) return;
      const intentHidden = event.type === 'T4sHeaderHide';
      if (record.headerIntentHidden === intentHidden) return;
      record.headerIntentHidden = intentHidden;
      record.headerIntentPending = true;
      record.headerIntentToken += 1;
      const intentToken = record.headerIntentToken;
      queueMicrotask(() => {
        if (record.observedHeader !== header || record.headerIntentToken !== intentToken) return;
        const headerState = syncHeaderMotionState(root, header);
        commitRenderedHeaderTarget(root, record, header, headerState, runningHeaderTransformMotion(header));
        requestAnimationFrame(() => {
          if (record.headerIntentToken === intentToken) record.headerIntentPending = false;
        });
      });
    };
    record.handleHeaderTransition = (event) => {
      if (event.target !== header || event.propertyName !== 'transform') return;
      measureStickyTop(root, record, event.type === 'transitioncancel'
        ? { trackMotion: true }
        : { settleMotion: true });
    };
    header.addEventListener('T4sHeaderHide', record.handleHeaderIntent);
    header.addEventListener('T4sHeaderReveal', record.handleHeaderIntent);
    header.addEventListener('transitionend', record.handleHeaderTransition);
    header.addEventListener('transitioncancel', record.handleHeaderTransition);
    return header;
  }

  function measureStickyTop(root, record, { settleMotion = false, trackMotion = false } = {}) {
    const header = bindObservedHeader(root, record);
    const headerState = syncHeaderMotionState(root, header);
    const isMobile = Boolean(record.mobileQuery?.matches);
    const next = isMobile
      ? 0
      : (trackMotion ? (headerState.hidden ? 0 : headerState.height) : renderedHeaderBottom(header));
    const sameSentinel = record.stickySentinel === root.querySelector('[data-artbox-filter-sentinel]');
    if (record.stickyTop !== next || !sameSentinel) {
      applyStickyTop(root, record, next);
    } else {
      recalibrateStickyState(root, record);
    }
    if (isMobile) {
      if (trackMotion) {
        commitRenderedHeaderTarget(root, record, header, headerState);
      } else if (settleMotion || !record.headerMotionActive) {
        clearStickyMotion(record);
        setHeaderMotion(root);
        setHeaderOffset(root, record, renderedHeaderBottom(header));
      }
      return;
    }
    clearStickyMotion(record);
    setHeaderMotion(root);
    setHeaderOffset(root, record, 0);
  }

  function initialiseSticky(root, record) {
    measureStickyTop(root, record);
  }

  function decorate(root, record) {
    decorateDeviceFinder(root);
    decorateFacetTabs(root);
    ensurePanelFacets(root).then(() => {
      decorateFacetTabs(root);
      syncCardDeviceLinks(root);
      root.dataset.artboxFacetsResolved = 'true';
      resizeFilterSheet(root, { animate: true });
    });
    decorateCards(root);
    observeSwatchImages(root, record);
    const familyGrid = root.querySelector('.artbox-collection__grid');
    if (record.familyGrid && record.familyGrid !== familyGrid) {
      window.ArtboxFamilyCatalog?.destroy(root);
      if (familyGrid?.dataset.artboxFamilyPage) {
        root.dataset.artboxFamilyPage = familyGrid.dataset.artboxFamilyPage;
      }
      if (familyGrid?.dataset.artboxFamilyPages) {
        root.dataset.artboxFamilyPages = familyGrid.dataset.artboxFamilyPages;
      }
    }
    record.familyGrid = familyGrid;
    record.familySession = window.ArtboxFamilyCatalog?.initialise(root) || null;
    initialiseSticky(root, record);
    root.setAttribute('data-artbox-collection-ready', 'true');
  }

  function lockSheetScroll(record) {
    if (record.sheetLocked) return;
    record.sheetScrollY = window.scrollY;
    record.sheetLocked = true;
    record.sheetLockStyles = {
      bodyOverflow: document.body.style.overflow,
      bodyOverscrollBehavior: document.body.style.overscrollBehavior,
      bodyTouchAction: document.body.style.touchAction,
    };
    document.documentElement.dataset.artboxFilterSheetOpen = 'true';
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'none';
    document.body.style.touchAction = 'none';
  }

  function unlockSheetScroll(record) {
    if (!record?.sheetLocked) return;
    const scrollY = record.sheetScrollY;
    const styles = record.sheetLockStyles || {};
    record.sheetLocked = false;
    record.sheetLockStyles = null;
    delete document.documentElement.dataset.artboxFilterSheetOpen;
    document.body.style.overflow = styles.bodyOverflow || '';
    document.body.style.overscrollBehavior = styles.bodyOverscrollBehavior || '';
    document.body.style.touchAction = styles.bodyTouchAction || '';
    window.scrollTo(0, scrollY);
  }

  function filterSheetMaxHeight() {
    const viewportHeight = window.visualViewport?.height || window.innerHeight;
    return Math.min(viewportHeight * 0.84, 760);
  }

  function resizeFilterSheet(root, { animate = false } = {}) {
    const record = records.get(root);
    const panel = root.querySelector('[data-artbox-filter-panel]');
    if (!record || record.filterMode !== 'sheet' || root.dataset.artboxFilterOpen !== 'true' || !panel || panel.hidden) return;
    if (record.sheetResizeFrame) cancelAnimationFrame(record.sheetResizeFrame);
    const currentHeight = panel.getBoundingClientRect().height;
    panel.dataset.artboxSheetMeasuring = 'true';
    panel.style.height = 'auto';
    const targetHeight = Math.min(panel.scrollHeight, filterSheetMaxHeight());
    panel.style.height = `${animate ? currentHeight : targetHeight}px`;
    panel.getBoundingClientRect();
    delete panel.dataset.artboxSheetMeasuring;
    if (!animate || Math.abs(currentHeight - targetHeight) < 1) {
      panel.style.height = `${targetHeight}px`;
      return;
    }
    record.sheetResizeFrame = requestAnimationFrame(() => {
      record.sheetResizeFrame = null;
      if (root.dataset.artboxFilterOpen === 'true' && record.filterMode === 'sheet') {
        panel.style.height = `${targetHeight}px`;
      }
    });
  }

  function syncResponsiveMode(root, record) {
    const nextMode = record.mobileQuery.matches ? 'sheet' : 'overlay';
    if (record.filterMode && record.filterMode !== nextMode && root.dataset.artboxFilterOpen === 'true') {
      closeFilterPanel(root, { restoreFocus: true });
    }
    record.filterMode = nextMode;
    root.dataset.artboxFilterMode = nextMode;
    syncDevicePresentation(root, nextMode === 'sheet');
    syncFacetAccordionMode(root);
    const panel = root.querySelector('[data-artbox-filter-panel]');
    if (!panel || nextMode === 'sheet') return;
    panel.removeAttribute('role');
    panel.removeAttribute('aria-modal');
  }

  function trapFilterSheetFocus(event, root, record) {
    if (event.key !== 'Tab' || record.filterMode !== 'sheet' || root.dataset.artboxFilterOpen !== 'true') return false;
    const panel = root.querySelector('[data-artbox-filter-panel]');
    const focusable = Array.from(panel?.querySelectorAll('button:not([disabled]), a[href], select:not([disabled]), input:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])') || [])
      .filter((element) => element.offsetParent !== null);
    if (!focusable.length) return false;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
      return true;
    }
    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
      return true;
    }
    return false;
  }

  function closeFilterPanel(root, { restoreFocus = false, pointerActivation = false } = {}) {
    const panel = root.querySelector('[data-artbox-filter-panel]');
    if (!panel) return;
    const record = records.get(root);
    const opener = root.querySelector('[data-artbox-filter-opener="true"]');
    if (record?.sheetResizeFrame) cancelAnimationFrame(record.sheetResizeFrame);
    if (!restoreFocus && panel.contains(document.activeElement)) document.activeElement.blur();
    panel.hidden = true;
    panel.classList.remove('is--filters-opened');
    panel.style.removeProperty('display');
    panel.style.removeProperty('height');
    delete panel.dataset.artboxSheetMeasuring;
    panel.removeAttribute('aria-busy');
    root.dataset.artboxFilterOpen = 'false';
    root.querySelectorAll(`[aria-controls="${panel.id}"]`).forEach((control) => {
      control.setAttribute('aria-expanded', 'false');
      control.removeAttribute('data-artbox-filter-opener');
    });
    panel.removeAttribute('role');
    panel.removeAttribute('aria-modal');
    if (restoreFocus) focusForInteraction(opener, record, pointerActivation);
    unlockSheetScroll(record);
  }

  async function openFilterPanel(root, trigger, facetKey = '', pointerActivation = false) {
    const panel = root.querySelector('[data-artbox-filter-panel]');
    if (!panel) return;
    root.querySelectorAll('[data-artbox-filter-opener="true"]').forEach((control) => {
      control.setAttribute('aria-expanded', 'false');
      control.removeAttribute('data-artbox-filter-opener');
    });
    panel.hidden = false;
    panel.classList.add('is--filters-opened');
    root.dataset.artboxFilterOpen = 'true';
    trigger.dataset.artboxFilterOpener = 'true';
    trigger.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-busy', 'true');
    const record = records.get(root);
    if (record?.filterMode === 'sheet') {
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      lockSheetScroll(record);
      resizeFilterSheet(root);
    }
    const facets = await ensurePanelFacets(root);
    if (!facets.length) {
      closeFilterPanel(root, { restoreFocus: false });
      if (panel.dataset.artboxFacetsLoaded === 'true') {
        trigger.hidden = true;
        delete root.dataset.artboxCoreFacetsReady;
        return;
      }
      trigger.dataset.artboxNativeFallback = 'true';
      trigger.click();
      delete trigger.dataset.artboxNativeFallback;
      return;
    }
    decorateFacetTabs(root);
    if (facetKey) selectFacetTab(root, facetKey);
    panel.removeAttribute('aria-busy');
    if (record?.filterMode === 'sheet') {
      resizeFilterSheet(root, { animate: true });
      focusForInteraction(panel.querySelector('button, a, select, input'), record, pointerActivation);
    }
  }

  function initialiseRoot(root) {
    if (records.has(root)) {
      decorate(root, records.get(root));
      return;
    }

    const record = {
      root,
      stickyTop: -1,
      stickyObserver: null,
      headerObserver: null,
      headerStateObserver: null,
      headerMotionFrame: null,
      headerMotionActive: false,
      headerIntentPending: false,
      headerIntentToken: 0,
      headerIntentHidden: null,
      headerOffset: -1,
      lastScrollY: window.scrollY,
      scrollDirection: '',
      observedHeader: null,
      handleHeaderIntent: null,
      stickySentinel: null,
      stickyThreshold: Number.NaN,
      filterMode: '',
      sheetLocked: false,
      sheetLockStyles: null,
      sheetResizeFrame: null,
      pointerFocusedElement: null,
      pointerDownTarget: null,
      pointerDownAt: 0,
      familyGrid: null,
      familySession: null,
      swatchImageObserver: null,
      observedSwatchTargets: new Set(),
    };
    records.set(root, record);
    bindObservedHeader(root, record);
    record.handleViewportChange = () => {
      measureStickyTop(root, record);
      resizeFilterSheet(root);
    };
    record.handleNavigationHeight = () => {
      measureStickyTop(root, record);
      recalibrateStickyState(root, record);
    };
    document.addEventListener('artbox:navigation:height', record.handleNavigationHeight);
    window.addEventListener('resize', record.handleViewportChange, { passive: true });
    window.addEventListener('orientationchange', record.handleViewportChange, { passive: true });
    record.mobileQuery = window.matchMedia('(max-width: 767px)');
    record.handleScroll = () => {
      if (!record.mobileQuery.matches) return;
      const currentScroll = window.scrollY;
      if (currentScroll > record.lastScrollY + 1) record.scrollDirection = 'down';
      else if (currentScroll < record.lastScrollY - 1) record.scrollDirection = 'up';
      record.lastScrollY = currentScroll;
      if (!record.headerMotionActive) {
        setHeaderOffset(root, record, renderedHeaderBottom(record.observedHeader || activeHeader()));
      }
      syncStickyState(root, record);
    };
    window.addEventListener('scroll', record.handleScroll, { passive: true });
    record.handleModeChange = () => {
      syncResponsiveMode(root, record);
      measureStickyTop(root, record);
    };
    record.mobileQuery.addEventListener('change', record.handleModeChange);
    syncResponsiveMode(root, record);
    document.fonts?.ready.then(() => {
      if (records.has(root)) measureStickyTop(root, record);
    });

    record.handleCaptureClick = (event) => {
      const clickTarget = event.target.closest?.('button, a, [role="button"]') || event.target;
      const pointerActivation = event.detail > 0
        || (record.pointerDownTarget === clickTarget && performance.now() - record.pointerDownAt < 2000);
      record.pointerDownTarget = null;
      event.artboxPointerActivation = pointerActivation;
      const sheetClose = event.target.closest?.('[data-artbox-filter-sheet-close]');
      if (sheetClose && root.contains(sheetClose)) {
        event.preventDefault();
        if (pointerActivation) sheetClose.blur();
        closeFilterPanel(root, { restoreFocus: !pointerActivation, pointerActivation });
        return;
      }
      const sheetReset = event.target.closest?.('[data-artbox-filter-reset]');
      if (sheetReset && root.contains(sheetReset)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        const toolbarReset = root.querySelector('.artbox-collection__reset');
        closeFilterPanel(root, { restoreFocus: false });
        if (toolbarReset) toolbarReset.click();
        else window.location.assign(sheetReset.href);
        return;
      }
      const filterTrigger = event.target.closest?.('[data-artbox-filter-toolbar] .t4s-btn-filter');
      if (filterTrigger && root.contains(filterTrigger)) {
        if (filterTrigger.dataset.artboxNativeFallback === 'true') return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (root.dataset.artboxFilterOpen === 'true') closeFilterPanel(root, { restoreFocus: false });
        else openFilterPanel(root, filterTrigger, '', pointerActivation);
        return;
      }
      const facetTab = event.target.closest?.('[data-artbox-facet-tab]');
      if (facetTab && root.contains(facetTab)) {
        event.preventDefault();
        openFilterPanel(root, facetTab, facetTab.dataset.artboxFacetTab, pointerActivation);
        return;
      }
      const facetAccordion = event.target.closest?.('[data-artbox-facet-accordion]');
      if (facetAccordion && root.contains(facetAccordion)) {
        if (record.filterMode !== 'sheet') return;
        event.preventDefault();
        toggleFacetAccordion(root, facetAccordion);
        return;
      }
      const deviceMore = event.target.closest?.('[data-artbox-device-more]');
      if (deviceMore && root.contains(deviceMore)) {
        event.preventDefault();
        const values = deviceMore.closest('.t4s-facet')?.querySelector('[data-artbox-device-values]');
        const expanded = values?.dataset.artboxDeviceExpanded === 'true';
        if (values) values.dataset.artboxDeviceExpanded = String(!expanded);
        deviceMore.setAttribute('aria-expanded', String(!expanded));
        deviceMore.textContent = expanded
          ? (root.querySelector('[data-artbox-filter-toolbar]')?.dataset.artboxMoreLabel || 'More')
          : 'Less';
        return;
      }
      const panelLink = event.target.closest?.('[data-artbox-filter-panel] a');
      if (!panelLink || !root.contains(panelLink)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      closeFilterPanel(root, { restoreFocus: false });
      activateFilterLink(root, panelLink);
    };

    record.handleClick = async (event) => {
      const pointerActivation = event.artboxPointerActivation ?? event.detail > 0;
      const chooserOpen = event.target.closest?.('[data-artbox-color-chooser-open]');
      if (chooserOpen && root.contains(chooserOpen)) {
        const card = chooserOpen.closest('[data-artbox-linked-card]');
        if (card) openSharedDialog(root, record, card, chooserOpen, pointerActivation);
        return;
      }

      const sharedDialog = event.target.closest?.('[data-artbox-shared-color-dialog]');
      if (sharedDialog && sharedDialog === record.sharedDialog) {
        if (event.target === sharedDialog || event.target.closest('[data-artbox-color-dialog-close]')) {
          closeSharedDialog(record, true, pointerActivation);
          return;
        }
        const dialogColor = event.target.closest('[data-artbox-linked-color]');
        if (dialogColor && record.dialogCard && selectLinkedProduct(record.dialogCard, dialogColor.dataset.artboxLinkedColor)) {
          closeSharedDialog(record, true, pointerActivation);
        }
        return;
      }

      const linkedColor = event.target.closest?.('[data-artbox-linked-color]');
      const linkedCard = linkedColor?.closest('[data-artbox-linked-card]');
      if (linkedColor && linkedCard && root.contains(linkedCard)) {
        selectLinkedProduct(linkedCard, linkedColor.dataset.artboxLinkedColor);
        return;
      }

      const finder = event.target.closest?.('[data-artbox-device-finder]');
      if (finder && root.contains(finder)) {
        const brandButton = event.target.closest('[data-artbox-device-brand]');
        if (brandButton) {
          setBrand(finder, brandButton.dataset.artboxDeviceBrand, true);
          return;
        }
        const changeButton = event.target.closest('[data-artbox-device-change]');
        if (changeButton) {
          finder.classList.toggle('is-expanded');
          syncDevicePresentation(root, record.filterMode === 'sheet');
          recalibrateStickyState(root, record);
          return;
        }
        const applyButton = event.target.closest('[data-artbox-device-apply]');
        if (applyButton) {
          const select = finder.querySelector('[data-artbox-device-select]');
          const value = select?.value;
          if (!value) return;
          writeDevice(value);
          updateDeviceSummary(finder, value);
          setFinderState(finder, 'remembered');
          finder.classList.remove('is-expanded');
          syncDevicePresentation(root, record.filterMode === 'sheet');
          recalibrateStickyState(root, record);
          await ensurePanelFacets(root);
          const link = findNativeFacetLink(finder.dataset.deviceFilterLabel, value)
            || Array.from(findPanelFacet(root, finder.dataset.deviceFilterLabel)?.querySelectorAll('a') || [])
              .find((candidate) => visibleFacetLabel(candidate) === value);
          activateFilterLink(root, link);
          return;
        }
        const browseButton = event.target.closest('[data-artbox-device-browse]');
        if (browseButton && browseButton.tagName !== 'A') {
          await ensurePanelFacets(root);
          const facet = findNativeFacet(finder.dataset.deviceFilterLabel) || findPanelFacet(root, finder.dataset.deviceFilterLabel);
          activateFilterLink(root, facet?.querySelector('li.is--selected a'));
        }
      }

    };

    record.handleOutsideClick = (event) => {
      if (record.dialogPendingInvoker && !record.dialogPendingInvoker.contains(event.target)) closeSharedDialog(record, false);
      const shell = root.querySelector('[data-artbox-filter-shell]');
      if (root.dataset.artboxFilterOpen === 'true' && shell && !shell.contains(event.target)) {
        closeFilterPanel(root, { restoreFocus: false });
      }
    };

    root.addEventListener('click', record.handleCaptureClick, true);
    root.addEventListener('click', record.handleClick);
    record.handlePointerDown = (event) => {
      record.pointerDownTarget = event.target.closest?.('button, a, [role="button"]') || event.target;
      record.pointerDownAt = performance.now();
      if (event.target.closest?.('[data-artbox-shared-color-dialog]')) return;
      clearPointerFocus(record);
    };
    root.addEventListener('pointerdown', record.handlePointerDown, true);
    record.handlePendingDialogKeydown = (event) => {
      if (event.key === 'Escape' && record.dialogPendingInvoker) {
        event.preventDefault();
        closeSharedDialog(record, false);
      }
    };
    document.addEventListener('keydown', record.handlePendingDialogKeydown, true);
    record.handleKeydown = (event) => {
      record.pointerDownTarget = null;
      clearPointerFocus(record);
      if (event.key === 'Escape' && root.dataset.artboxFilterOpen === 'true') {
        event.preventDefault();
        closeFilterPanel(root, { restoreFocus: true });
        return;
      }
      if (trapFilterSheetFocus(event, root, record)) return;
      trapDialogFocus(event, record);
    };
    root.addEventListener('keydown', record.handleKeydown);
    document.addEventListener('click', record.handleOutsideClick);
    record.contentObserver = new MutationObserver(() => decorate(root, record));
    record.contentObserver.observe(root, { childList: true, subtree: true });
    decorate(root, record);
  }

  function initialise(scope) {
    const roots = [];
    if (scope.matches?.('[data-artbox-collection]')) roots.push(scope);
    roots.push(...(scope.querySelectorAll?.('[data-artbox-collection]') || []));
    roots.forEach(initialiseRoot);
  }

  function unload(event) {
    const root = event.target?.matches?.('[data-artbox-collection]')
      ? event.target
      : event.target?.querySelector?.('[data-artbox-collection]');
    const record = root ? records.get(root) : null;
    if (!record) return;
    record.contentObserver?.disconnect();
    closeSharedDialog(record, false);
    record.swatchImageObserver?.disconnect();
    record.observedSwatchTargets.clear();
    window.ArtboxFamilyCatalog?.destroy(root);
    record.stickyObserver?.disconnect();
    unbindObservedHeader(record);
    clearStickyMotion(record);
    window.removeEventListener('resize', record.handleViewportChange);
    window.removeEventListener('orientationchange', record.handleViewportChange);
    document.removeEventListener('artbox:navigation:height', record.handleNavigationHeight);
    window.removeEventListener('scroll', record.handleScroll);
    record.mobileQuery?.removeEventListener('change', record.handleModeChange);
    root.removeEventListener('click', record.handleCaptureClick, true);
    root.removeEventListener('click', record.handleClick);
    root.removeEventListener('pointerdown', record.handlePointerDown, true);
    root.removeEventListener('keydown', record.handleKeydown);
    document.removeEventListener('keydown', record.handlePendingDialogKeydown, true);
    document.removeEventListener('click', record.handleOutsideClick);
    unlockSheetScroll(record);
    clearPointerFocus(record);
    records.delete(root);
  }

  const lifecycle = { initialise, unload };
  window[lifecycleKey] = lifecycle;
  document.addEventListener('shopify:section:load', (event) => {
    initialise(event.target);
    document.querySelectorAll('[data-artbox-collection]').forEach((root) => {
      const record = records.get(root);
      if (record) measureStickyTop(root, record);
    });
  });
  document.addEventListener('shopify:section:unload', unload);
  const restoreColorMemory = () => document.querySelectorAll('[data-artbox-collection]').forEach((root) => decorateCards(root, { restoreMemory: true }));
  window.addEventListener('pageshow', restoreColorMemory);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') restoreColorMemory();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(document), { once: true });
  } else {
    initialise(document);
  }
})();
